import React, { useState, useEffect, Fragment } from "react";
import { Tab } from "@headlessui/react";
import {
  XMarkIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import useBotStrategyStore from "../../stores/bot";
import ConfirmationModal from "./ConfirmationModal";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const BotSettingsModal = ({
  bot,
  isViewMode,
  show,
  onClose,
  onSave,
  onSuccess, // ✅ new callback
  isSubmitting,
  isLoading,
}) => {
  const { deleteBotProfile, toggleBotProfileStatus } = useBotStrategyStore();
  const [dataByLevel, setDataByLevel] = useState({});
  const [selectedLevel, setSelectedLevel] = useState("low");
  const [isProcessing, setIsProcessing] = useState(false);

  // confirmation dialog
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // 🔄 Normalize API data
  useEffect(() => {
    if (!bot) return;

    if (bot.parameters_grouped) {
      setDataByLevel(bot.parameters_grouped);
      const levels = Object.keys(bot.parameters_grouped);
      if (levels.length > 0) setSelectedLevel(levels[0]);
    } else if (bot.profiles) {
      setDataByLevel({ [bot.parameter_level]: bot.profiles });
      setSelectedLevel(bot.parameter_level);
    }
  }, [bot]);

  // 🧩 Handle field changes
  const handleParamChange = (level, profileIndex, key, value) => {
    setDataByLevel((prev) => {
      const updatedProfiles = prev[level].map((p, i) =>
        i === profileIndex
          ? {
              ...p,
              parameters: p.parameters.map((param) =>
                param.parameter_key === key
                  ? { ...param, parameter_value: value }
                  : param
              ),
            }
          : p
      );
      return { ...prev, [level]: updatedProfiles };
    });
  };

  const handleMetaChange = (level, profileIndex, field, value) => {
    setDataByLevel((prev) => {
      const updatedProfiles = prev[level].map((p, i) =>
        i === profileIndex ? { ...p, [field]: value } : p
      );
      return { ...prev, [level]: updatedProfiles };
    });
  };

  // 💾 Save button
  const handleSave = (profile) => {
    const payload = {
      bot_master_id: bot.bot_master_id,
      parameter_level: selectedLevel,
      parameters: profile.parameters.map((p) => ({
        parameter_key: p.parameter_key,
        parameter_value: p.parameter_value,
      })),
      name: profile.name,
      description: profile.description,
      coin_id: profile.coin?.id ?? 0,
    };
    onSave(profile.profile_group_id, payload);
  };

  // 🧩 Open confirmation dialog
  const openConfirm = (type, profile) => {
    setConfirmType(type);
    setSelectedProfile(profile);
    setShowConfirm(true);
  };

  // 🧩 Confirm actions (delete/toggle)
  const handleConfirmAction = async () => {
    if (!selectedProfile) return;
    setIsProcessing(true);
    let success = false;
    let message = "";

    if (confirmType === "delete") {
      success = await deleteBotProfile(selectedProfile.profile_group_id);
      message = `Profile "${selectedProfile.name}" deleted successfully.`;
    } else if (confirmType === "toggle") {
      success = await toggleBotProfileStatus(selectedProfile.profile_group_id);
      message = `Profile "${selectedProfile.name}" is now ${
        selectedProfile.is_active ? "inactive" : "active"
      }.`;
    }

    setIsProcessing(false);
    setShowConfirm(false);

    if (success) {
      onSuccess?.(message); // ✅ notify parent
      onClose();
    }
  };

  if (!show) return null;

  if (isLoading)
    return (
      <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/50">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-gray-700 font-medium">Loading bot details...</p>
        </div>
      </div>
    );

  if (!Object.keys(dataByLevel).length) return null;

  const levels = Object.keys(dataByLevel);

  return (
    <div
      className="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-5 pb-4 sm:p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {isViewMode ? "View" : "Edit"} Settings:{" "}
                <span className="font-bold">{bot.bot_master_bot_name}</span>
              </h3>
              <button
                onClick={onClose}
                type="button"
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-white px-6 pt-5 pb-6 sm:p-6">
            <Tab.Group onChange={(index) => setSelectedLevel(levels[index])}>
              <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-4">
                {levels.map((level) => (
                  <Tab key={level} as={Fragment}>
                    {({ selected }) => (
                      <button
                        className={classNames(
                          "w-full rounded-lg py-2.5 text-sm font-medium leading-5 capitalize",
                          selected
                            ? "bg-white text-blue-700 shadow"
                            : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
                        )}
                      >
                        {level.replace("_", " ")}
                      </button>
                    )}
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels className="max-h-[70vh] overflow-y-auto space-y-6">
                {levels.map((level) => (
                  <Tab.Panel key={level}>
                    {dataByLevel[level].map((profile, profileIndex) => (
                      <div
                        key={profile.profile_group_id}
                        className="border border-gray-200 rounded-lg p-4 mb-5 shadow-sm"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-base font-semibold text-gray-800">
                            {profile.name}
                          </h4>
                          {profile.is_active ? (
                            <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircleIcon className="h-4 w-4" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                              <XCircleIcon className="h-4 w-4" />
                              Inactive
                            </span>
                          )}
                        </div>

                        {/* Editable Fields */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 text-left">
                              Name
                            </label>
                            <input
                              type="text"
                              value={profile.name}
                              disabled={isViewMode}
                              onChange={(e) =>
                                handleMetaChange(
                                  level,
                                  profileIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 text-left">
                              Description
                            </label>
                            <input
                              type="text"
                              value={profile.description}
                              disabled={isViewMode}
                              onChange={(e) =>
                                handleMetaChange(
                                  level,
                                  profileIndex,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            />
                          </div>
                        </div>

                        {/* Parameters */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {profile.parameters.map((param) => (
                            <div key={param.parameter_key}>
                              <label className="block text-sm font-medium text-gray-700 capitalize text-left">
                                {param.parameter_key.replace(/_/g, " ")}
                              </label>
                              <input
                                type="text"
                                value={param.parameter_value || ""}
                                disabled={isViewMode}
                                onChange={(e) =>
                                  handleParamChange(
                                    level,
                                    profileIndex,
                                    param.parameter_key,
                                    e.target.value
                                  )
                                }
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                              />
                            </div>
                          ))}
                        </div>

                        {/* Footer Actions */}
                        <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
                          <p>
                            <strong>Coin:</strong> {profile.coin?.name}/
                            {profile.coin?.base_currency}
                          </p>

                          {!isViewMode && (
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleSave(profile)}
                                disabled={isSubmitting}
                                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-blue-400"
                              >
                                {isSubmitting ? "Saving..." : "Save"}
                              </button>

                              <button
                                onClick={() => openConfirm("toggle", profile)}
                                className={`px-3 py-2 rounded-md text-sm text-white ${
                                  profile.is_active
                                    ? "bg-yellow-600 hover:bg-yellow-700"
                                    : "bg-green-600 hover:bg-green-700"
                                }`}
                              >
                                {profile.is_active ? "Deactivate" : "Activate"}
                              </button>

                              <button
                                onClick={() => openConfirm("delete", profile)}
                                className="px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 flex items-center gap-1"
                              >
                                <TrashIcon className="h-4 w-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 text-right border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {isViewMode ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationModal
        show={showConfirm}
        title={
          confirmType === "delete"
            ? "Delete Profile"
            : selectedProfile?.is_active
            ? "Deactivate Profile"
            : "Activate Profile"
        }
        message={
          confirmType === "delete"
            ? `Are you sure you want to delete "${selectedProfile?.name}"?`
            : `Are you sure you want to ${
                selectedProfile?.is_active ? "deactivate" : "activate"
              } "${selectedProfile?.name}"?`
        }
        confirmText={
          confirmType === "delete"
            ? "Delete"
            : selectedProfile?.is_active
            ? "Deactivate"
            : "Activate"
        }
        cancelText="Cancel"
        isProcessing={isProcessing}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default BotSettingsModal;
